import React, { useContext, useState } from 'react';
import { Button, Popover, Spin, Typography } from 'antd';
import { useSelector } from 'react-redux';
import {
    Grid,
    SearchBar,
    SearchContext,
    SearchContextManager,
    SuggestionBar,
} from '@giphy/react-components';
import type { IGif } from '@giphy/js-types';
import type { RootState } from '../store';
import { GIPHY_API_KEY } from '../constants';
import { giphyToFile } from '../utils/giphy';
import { getNotificationInstance } from '../utils/notification';

const { Text } = Typography;
const PICKER_WIDTH = 320;

interface CommentGiphyPickerProps {
    onSelect: (file: File) => void;
    disabled?: boolean;
}

const GiphyPickerBody: React.FC<{
    onPick: (gif: IGif) => void;
    loading: boolean;
}> = ({ onPick, loading }) => {
    const { fetchGifs, searchKey } = useContext(SearchContext);

    return (
        <div className="task-giphy-picker">
            <SearchBar placeholder="Поиск GIF…" autoFocus={false} />
            <SuggestionBar />
            <div className="task-giphy-picker__grid">
                <Grid
                    key={searchKey}
                    width={PICKER_WIDTH - 16}
                    columns={2}
                    gutter={6}
                    fetchGifs={fetchGifs}
                    noLink
                    user={{}}
                    onGifClick={(gif, event) => {
                        event.preventDefault();
                        onPick(gif);
                    }}
                    noResultsMessage="Ничего не найдено"
                />
            </div>
            <div className="task-giphy-picker__brand">Powered by GIPHY</div>
            {loading ? (
                <div className="task-giphy-picker__loading">
                    <Spin />
                </div>
            ) : null}
        </div>
    );
};

const CommentGiphyPicker: React.FC<CommentGiphyPickerProps> = ({ onSelect, disabled }) => {
    const isDark = useSelector((state: RootState) => Boolean(state.settings.theme?.isDark));
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handlePick = async (gif: IGif) => {
        if (loading) {
            return;
        }
        setLoading(true);
        try {
            const file = await giphyToFile(gif);
            onSelect(file);
            setOpen(false);
        } catch (error) {
            const description = error instanceof Error
                ? error.message
                : 'Не удалось добавить GIF';
            getNotificationInstance()?.error({
                message: 'Ошибка',
                description,
            });
        } finally {
            setLoading(false);
        }
    };

    const content = GIPHY_API_KEY
        ? (
            <SearchContextManager
                apiKey={GIPHY_API_KEY}
                shouldDefaultToTrending
                options={{
                    lang: 'ru',
                    rating: 'pg',
                    type: 'gifs',
                    limit: 12,
                }}
                theme={{
                    darkMode: isDark,
                    hideCancelButton: true,
                }}
            >
                <GiphyPickerBody onPick={handlePick} loading={loading} />
            </SearchContextManager>
        )
        : (
            <div className="task-giphy-picker task-giphy-picker--empty">
                <Text type="secondary">
                    Задайте REACT_APP_GIPHY_API_KEY в настройках фронтенда, чтобы искать GIF.
                </Text>
            </div>
        );

    return (
        <Popover
            trigger="click"
            open={open}
            onOpenChange={(next) => {
                if (loading && !next) {
                    return;
                }
                setOpen(next);
            }}
            placement="topRight"
            arrow={false}
            zIndex={2000}
            getPopupContainer={() => document.body}
            styles={{ body: { padding: 0 } }}
            content={content}
        >
            <Button
                type="text"
                shape="circle"
                className="task-comments__gif"
                disabled={disabled}
                aria-label="GIF"
                title="GIF"
            >
                <span className="task-comments__gif-label">GIF</span>
            </Button>
        </Popover>
    );
};

export default CommentGiphyPicker;
